import type { MouseEvent } from 'react'
import { Camera, Image as ImageIcon, Lock } from 'lucide-react'
import type { WireArticle } from '../../../api/types'
import { useWireAuth } from '../../../auth/useWireAuth'
import { isArticleLocked } from '../../../lib/articleAccess'

export type SectionHeaderStyle = 'rule-left' | 'all-caps' | 'plain'

export type StoryRowImageAspectRatio = '16/9' | '4/3' | '1/1' | '3/4'

export interface StoryRowConfig {
  columnCount: 2 | 3 | 4 | 5
  imageAspectRatio: StoryRowImageAspectRatio
  sectionHeaderEnabled: boolean
  sectionHeaderTitle: string | null
  sectionHeaderStyle: SectionHeaderStyle
  showImages: boolean
  showSummary: boolean
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })
  } catch {
    return iso
  }
}

export interface StoryRowProps {
  articles: WireArticle[]
  config?: Partial<StoryRowConfig>
  className?: string
  onArticleClick?: (article: WireArticle, index: number, event: MouseEvent<HTMLElement>) => void
  activeArticleId?: string | null
}

const DEFAULT_CONFIG: StoryRowConfig = {
  columnCount: 3,
  imageAspectRatio: '4/3',
  sectionHeaderEnabled: false,
  sectionHeaderTitle: null,
  sectionHeaderStyle: 'rule-left',
  showImages: true,
  showSummary: false,
}

// Density scales with column count — fewer columns = more breathing room
const DENSITY = {
  2: { padding: 'p-3', headline: 'text-base',   meta: 'text-[10px]', iconSize: 28 },
  3: { padding: 'p-3', headline: 'text-sm',      meta: 'text-[10px]', iconSize: 28 },
  4: { padding: 'p-2', headline: 'text-[13px]',  meta: 'text-[9px]',  iconSize: 24 },
  5: { padding: 'p-2', headline: 'text-[13px]',  meta: 'text-[9px]',  iconSize: 24 },
} as const

const GRID_CLASS: Record<2 | 3 | 4 | 5, string> = {
  2: 'grid gap-y-4 md:grid-cols-2 md:gap-y-0',
  3: 'grid gap-y-4 md:grid-cols-3 md:gap-y-0',
  4: 'grid gap-y-4 sm:grid-cols-2 sm:gap-y-4 lg:grid-cols-4 lg:gap-y-0',
  5: 'grid gap-y-4 sm:grid-cols-2 sm:gap-y-4 lg:grid-cols-5 lg:gap-y-0',
}

const BORDER: Record<2 | 3 | 4 | 5, { line: string; right: string }> = {
  2: { line: 'md:border-line', right: 'md:border-r' },
  3: { line: 'md:border-line', right: 'md:border-r' },
  4: { line: 'lg:border-line', right: 'lg:border-r' },
  5: { line: 'lg:border-line', right: 'lg:border-r' },
}

const ASPECT_CLASS: Record<StoryRowImageAspectRatio, string> = {
  '16/9': 'aspect-[16/9]',
  '4/3':  'aspect-[4/3]',
  '1/1':  'aspect-square',
  '3/4':  'aspect-[3/4]',
}

function renderSectionHeader(title: string, style: SectionHeaderStyle) {
  if (style === 'all-caps') {
    return (
      <header className="mb-3">
        <h3 className="text-xs font-bold uppercase tracking-widest text-fg-secondary">{title}</h3>
      </header>
    )
  }
  if (style === 'plain') {
    return (
      <header className="mb-3">
        <h3 className="text-sm font-semibold text-fg">{title}</h3>
      </header>
    )
  }
  return (
    <header className="mb-3 border-b border-line pb-1">
      <h3 className="text-xs font-bold uppercase tracking-wider text-fg-secondary">{title}</h3>
    </header>
  )
}

function EmptyCard({
  index,
  aspectClass,
  padding,
}: {
  index: number
  aspectClass: string
  padding: string
}) {
  return (
    <article className={padding}>
      <div className={`mb-2 flex ${aspectClass} items-center justify-center bg-fill text-xs text-fg-secondary`}>
        Slot {index + 1}
      </div>
    </article>
  )
}

function ArticleCard({
  article,
  index,
  locked,
  showImages,
  showSummary,
  aspectClass,
  padding,
  headlineClass,
  metaClass,
  iconSize,
  onArticleClick,
  activeArticleId,
}: {
  article: WireArticle
  index: number
  locked?: boolean
  showImages: boolean
  showSummary: boolean
  aspectClass: string
  padding: string
  headlineClass: string
  metaClass: string
  iconSize: number
  onArticleClick?: (article: WireArticle, index: number, event: MouseEvent<HTMLElement>) => void
  activeArticleId?: string | null
}) {
  const isRecalled = article.workflow?.stage === 'recalled'
  const displayTitle = isRecalled ? 'This Article Has Been Recalled' : article.title
  const thumbnailUrl = isRecalled ? undefined : article.thumbnail?.presignedUrl

  const image = showImages ? (
    thumbnailUrl ? (
      <img
        src={thumbnailUrl}
        alt={article.thumbnail?.altText ?? ''}
        className={`mb-2 ${aspectClass} w-full object-cover`}
        style={article.thumbnail?.focalPoint
          ? { objectPosition: `${article.thumbnail.focalPoint.x * 100}% ${article.thumbnail.focalPoint.y * 100}%` }
          : undefined}
        loading="lazy"
      />
    ) : locked ? (
      <div className={`mb-2 flex ${aspectClass} w-full items-center justify-center bg-fill`}>
        <Lock size={36} className="text-warning-fg" aria-hidden="true" />
      </div>
    ) : (
      <div className={`mb-2 flex ${aspectClass} items-center justify-center bg-fill`}>
        <ImageIcon size={iconSize} className="text-fg-secondary" aria-hidden="true" />
      </div>
    )
  ) : null

  return (
    <article
      className={[
        `bg-transparent ${padding} transition-all duration-200 hover:bg-fill-hover/40`,
        activeArticleId === article.id ? 'bg-accent-primary-fill/45 ring-2 ring-inset ring-accent-primary-solid shadow-sm' : '',
        activeArticleId && activeArticleId !== article.id ? 'opacity-35 saturate-50' : '',
      ].join(' ')}
      onClick={(event) => onArticleClick?.(article, index, event)}
    >
      {image}
      <div className={`mb-1 flex items-center gap-1.5 ${metaClass} font-semibold uppercase tracking-widest text-fg-secondary`}>
        {article.regions?.[0] ? <span>{article.regions[0]}</span> : null}
        <Camera size={10} aria-hidden="true" />
        <span>{article.thumbnail?.classification ? [article.thumbnail.classification, ...(article.thumbnail.disseminationControls ?? [])].join('//') : [article.classification, ...(article.disseminationControls ?? [])].join('//')}</span>
        {article.thumbnail?.credit && (
          <span className="font-normal normal-case tracking-normal opacity-70">{article.thumbnail.credit}</span>
        )}
      </div>
      <h4 className={`font-zodiak mt-1 ${headlineClass} font-bold leading-snug text-fg`}>
        {locked && <Lock size={11} className="mr-0.5 inline-block align-middle text-warning-fg" aria-label="Access restricted" />}
        ({[article.classification, ...(article.disseminationControls ?? [])].join('//')}) {displayTitle}
      </h4>
      {!isRecalled && article.sensitive ? (
        <p className="mt-1 inline-flex items-center rounded bg-red-9 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-white">
          Sensitive
        </p>
      ) : null}
      {showSummary && article.description ? (
        <p className={`mt-1 ${metaClass} leading-relaxed text-fg-secondary`}>{article.description}</p>
      ) : null}
      {article.publishedAt ? (
        <p className={`mt-2 ${metaClass} uppercase tracking-wide text-fg-secondary`}>{formatDate(article.publishedAt)}</p>
      ) : article.byline ? (
        <p className={`mt-2 ${metaClass} uppercase tracking-wide text-fg-secondary`}>{article.byline}</p>
      ) : null}
    </article>
  )
}

export function StoryRow({ articles, config, className, onArticleClick, activeArticleId }: StoryRowProps) {
  const { user } = useWireAuth()
  const merged = { ...DEFAULT_CONFIG, ...config }
  // columnCount may arrive as a string from the editor's select field
  const rawCount = Number(merged.columnCount)
  const columnCount = ([2, 3, 4, 5].includes(rawCount) ? rawCount : 3) as 2 | 3 | 4 | 5

  const { padding, headline: headlineClass, meta: metaClass, iconSize } = DENSITY[columnCount]
  const aspectClass = ASPECT_CLASS[merged.imageAspectRatio] ?? ASPECT_CLASS['4/3']
  const gridClass = GRID_CLASS[columnCount]
  const { line: borderLine, right: borderRight } = BORDER[columnCount]

  const cards = articles.slice(0, columnCount)
  while (cards.length < columnCount) {
    cards.push(undefined as unknown as WireArticle)
  }

  return (
    <section className={className}>
      {merged.sectionHeaderEnabled && merged.sectionHeaderTitle
        ? renderSectionHeader(merged.sectionHeaderTitle, merged.sectionHeaderStyle)
        : null}
      <div className={gridClass}>
        {cards.map((article, index) => (
          <div
            key={article ? article.id : `empty-${index}`}
            className={`${borderLine} ${index < cards.length - 1 ? borderRight : ''}`}
          >
            {article ? (
              <ArticleCard
                article={article}
                index={index}
                locked={Boolean(user && isArticleLocked(article, user))}
                showImages={merged.showImages}
                showSummary={merged.showSummary}
                aspectClass={aspectClass}
                padding={padding}
                headlineClass={headlineClass}
                metaClass={metaClass}
                iconSize={iconSize}
                onArticleClick={onArticleClick}
                activeArticleId={activeArticleId}
              />
            ) : (
              <EmptyCard index={index} aspectClass={aspectClass} padding={padding} />
            )}
          </div>
        ))}
      </div>
    </section>
  )
}
